import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./agendaWidget.style.css";
import api from "../../Api";
import Loader from "../Loader/Loader";

function AgendaWidget({ auth }) {
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // Use a ref to avoid re-creating the callback on every render
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!auth?.userId) {
            setLoading(false);
            setError(t('dashboard.couldNotLoadAssignments'));
            return;
        }

        const fetchAssignments = async () => {
            try {
                setLoading(true);

                // Get date range for current year (to show all user's assignments)
                const now = new Date();
                const startOfYear = new Date(now.getFullYear(), 0, 1);
                const endOfYear = new Date(now.getFullYear(), 11, 31);

                const startDate = startOfYear.toISOString().split('T')[0];
                const endDate = endOfYear.toISOString().split('T')[0];

                const response = await api.get(`/project-user-assignments?start_date=${startDate}&end_date=${endDate}`);

                // Filter to only show assignments for the logged-in user
                const userAssignments = (response.data || []).filter(
                    assignment => assignment.user_id === auth?.userId
                );

                if (isMountedRef.current) {
                    setAssignments(userAssignments);
                }
            } catch (err) {
                console.error("Error fetching assignments:", err);
                if (isMountedRef.current) {
                    setError(t('dashboard.couldNotLoadAssignments'));
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        };

        fetchAssignments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.userId]); // Only depend on auth.userId to prevent infinite loop (t function changes on every render)

    // Separate callback for manual refetch (e.g., retry button)
    const refetchAssignments = useCallback(async () => {
        if (!auth?.userId) return;

        try {
            setLoading(true);
            setError(null);

            const now = new Date();
            const startOfYear = new Date(now.getFullYear(), 0, 1);
            const endOfYear = new Date(now.getFullYear(), 11, 31);

            const startDate = startOfYear.toISOString().split('T')[0];
            const endDate = endOfYear.toISOString().split('T')[0];

            const response = await api.get(`/project-user-assignments?start_date=${startDate}&end_date=${endDate}`);

            const userAssignments = (response.data || []).filter(
                assignment => assignment.user_id === auth?.userId
            );
            setAssignments(userAssignments);
        } catch (err) {
            console.error("Error fetching assignments:", err);
            setError(t('dashboard.couldNotLoadAssignments'));
        } finally {
            setLoading(false);
        }
    }, [auth?.userId, t]);

    // If no auth, show error immediately
    if (!auth?.userId && !loading) {
        return (
            <div className="agenda-widget-error" role="alert">
                <p className="error-message">Geen gebruiker ingelogd</p>
            </div>
        );
    }

    if (loading) return <Loader message="Agenda laden..." />;
    if (error) return (
        <div className="agenda-widget-error" role="alert">
            <p className="error-message">{error}</p>
            <button
                type="button"
                className="btn btn-submit"
                onClick={refetchAssignments}
                aria-label="Opnieuw proberen agenda te laden"
            >
                Opnieuw proberen
            </button>
        </div>
    );

    // Get assignments for a specific date
    const getAssignmentsForDate = (date) => {
        if (!assignments || assignments.length === 0) return [];

        return assignments.filter(assignment => {
            const startDate = new Date(assignment.start_date);
            const endDate = new Date(assignment.end_date);
            const checkDate = new Date(date);

            // Reset time parts for accurate comparison
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            checkDate.setHours(0, 0, 0, 0);

            return checkDate >= startDate && checkDate <= endDate;
        });
    };

    // Generate calendar days for current month (starting on Sunday)
    const generateCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        // Start on Sunday (getDay() returns 0 for Sunday, so this works correctly)
        startDate.setDate(startDate.getDate() - startDate.getDay());

        const days = [];
        const endDate = new Date(lastDay);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            days.push({
                date: new Date(date),
                isCurrentMonth: date.getMonth() === month
            });
        }

        return days;
    };

    const calendarDays = generateCalendarDays();
    const selectedDayAssignments = getAssignmentsForDate(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const monthYearFormat = currentMonth.toLocaleDateString('nl-NL', {
        month: 'long',
        year: 'numeric'
    });

    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const goToToday = () => {
        const now = new Date();
        setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
        setSelectedDate(now);
    };

    if (loading) return <Loader message={t('common.loading')} />;
    if (error) return <div className="error-message" role="alert">{error}</div>;

    return (
        <section className="agenda-widget card" aria-labelledby="agenda-widget-title">
            <header className="agenda-widget-header card-header">
                <h3 id="agenda-widget-title" className="card-title">
                    <span aria-hidden="true">📅</span> {t('dashboard.myAgenda')}
                </h3>
            </header>

            <div className="agenda-widget-content">
                {/* Calendar Section */}
                <div className="widget-calendar-section">
                    <div className="widget-calendar-controls">
                        <button
                            type="button"
                            onClick={goToPreviousMonth}
                            className="btn btn-secondary btn-widget-nav"
                            aria-label="Vorige maand"
                        >
                            <span aria-hidden="true">←</span>
                        </button>
                        <h4 className="widget-month-title">
                            {monthYearFormat}
                        </h4>
                        <button
                            type="button"
                            onClick={goToNextMonth}
                            className="btn btn-secondary btn-widget-nav"
                            aria-label="Volgende maand"
                        >
                            <span aria-hidden="true">→</span>
                        </button>
                        <button
                            type="button"
                            onClick={goToToday}
                            className="btn btn-secondary btn-today"
                        >
                            {t('agenda.today')}
                        </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="widget-calendar-header" role="row">
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.sun')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.mon')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.tue')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.wed')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.thu')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.fri')}</div>
                        <div className="widget-day-name" role="columnheader">{t('agenda.days.sat')}</div>
                    </div>

                    <div className="widget-calendar-grid" role="grid" aria-label="Kalender">{calendarDays.map((day, index) => {
                            const dayAssignments = getAssignmentsForDate(day.date);
                            const isToday = day.date.getTime() === today.getTime();
                            const isSelected = day.date.getTime() === selectedDate.getTime();
                            const hasAssignments = dayAssignments.length > 0;

                            return (
                                <button
                                    key={index}
                                    type="button"
                                    className={`widget-calendar-day 
                                        ${!day.isCurrentMonth ? "other-month" : ""} 
                                        ${isToday ? "today" : ""} 
                                        ${isSelected ? "selected" : ""}
                                        ${hasAssignments ? "has-assignments" : ""}`}
                                    onClick={() => setSelectedDate(day.date)}
                                    aria-label={`${day.date.toLocaleDateString('nl-NL', { 
                                        day: 'numeric', 
                                        month: 'long' 
                                    })}${hasAssignments ? `, ${dayAssignments.length} toewijzing${dayAssignments.length > 1 ? 'en' : ''}` : ''}`}
                                    aria-current={isSelected ? "date" : undefined}
                                >
                                    <span className="widget-day-number" aria-hidden="true">
                                        {day.date.getDate()}
                                    </span>
                                    {hasAssignments && (
                                        <span
                                            className="widget-assignment-dot"
                                            aria-hidden="true"
                                            title={`${dayAssignments.length} toewijzing${dayAssignments.length > 1 ? 'en' : ''}`}
                                        >
                                            {dayAssignments.length}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Assignments Detail Section */}
                <section className="widget-assignments-section" aria-labelledby="assignments-date-heading">
                    <header className="widget-assignments-header">
                        <h4 id="assignments-date-heading">
                            <time dateTime={selectedDate.toISOString()}>
                                {selectedDate.toLocaleDateString('nl-NL', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </time>
                        </h4>
                    </header>

                    <div className="widget-assignments-list">
                        {selectedDayAssignments.length === 0 ? (
                            <div className="no-assignments-message text-center p-5" role="status">
                                <div className="no-assignments-icon text-4xl mb-3" aria-hidden="true"></div>
                                <p className="text-base text-muted mb-2">{t('dashboard.noAssignments')}</p>
                                <small className="text-sm text-muted">{t('agenda.selectDate')}</small>
                            </div>
                        ) : (
                            <>
                                <div className="assignments-header flex justify-between items-center mb-4">
                                    <h5 className="font-semibold">
                                        {t('dashboard.myAssignments')}
                                    </h5>
                                    <span className="assignments-count badge">
                                        {selectedDayAssignments.length} {selectedDayAssignments.length === 1 ? t('project.title').toLowerCase() : t('nav.projects').toLowerCase()}
                                    </span>
                                </div>
                                {selectedDayAssignments.map((assignment, index) => (
                                    <article key={assignment.id} className="project-assignment-card card mb-4">
                                        <header className="project-header">
                                            <div className="project-title">
                                                <span className="project-number badge" aria-hidden="true">
                                                    {index + 1}
                                                </span>
                                                <h6 className="font-semibold">
                                                    {assignment.project?.name || assignment.project_name || t('project.title')}
                                                </h6>
                                            </div>
                                            <span className="project-dates text-sm text-muted">
                                                <time dateTime={assignment.start_date}>
                                                    {new Date(assignment.start_date).toLocaleDateString(t('common.locale'), { day: 'numeric', month: 'short' })}
                                                </time>
                                                {' - '}
                                                <time dateTime={assignment.end_date}>
                                                    {new Date(assignment.end_date).toLocaleDateString(t('common.locale'), { day: 'numeric', month: 'short' })}
                                                </time>
                                            </span>
                                        </header>

                                        <div className="project-details-grid">
                                            {assignment.project?.client && (
                                                <div className="project-info-item">
                                                    <div className="info-content">
                                                        <span className="info-label text-sm text-muted">
                                                            {t('common.client')}
                                                        </span>
                                                        <span className="info-value">
                                                            {assignment.project.client}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {assignment.project?.address && (
                                                <div className="project-info-item full-width">
                                                    <div className="info-content">
                                                        <span className="info-label text-sm text-muted">
                                                            {t('common.address')}
                                                        </span>
                                                        <address className="info-value not-italic">
                                                            {assignment.project.address}
                                                        </address>
                                                        {assignment.project.location && (
                                                            <span className="info-sublabel text-sm">
                                                                {assignment.project.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {assignment.project?.whatsapp && (
                                                <div className="project-info-item">
                                                    <div className="info-content">
                                                        <span className="info-label text-sm text-muted">
                                                            WhatsApp
                                                        </span>
                                                        <a
                                                            href={`https://wa.me/${assignment.project.whatsapp.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="whatsapp-link text-primary"
                                                            aria-label={`WhatsApp naar ${assignment.project.whatsapp}`}
                                                        >
                                                            {assignment.project.whatsapp}
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {assignment.notes && (
                                            <div className="project-notes mt-3">
                                                <span className="notes-icon" aria-hidden="true">📝</span>
                                                <div className="notes-content">
                                                    <span className="notes-label text-sm font-semibold">
                                                        {t('timesheet.notes')}
                                                    </span>
                                                    <p className="notes-text text-sm">
                                                        {assignment.notes}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </article>
                                ))}
                            </>
                        )}
                    </div>
                </section>
            </div>
        </section>
    );
}

AgendaWidget.propTypes = {
    auth: PropTypes.object.isRequired
};

export default AgendaWidget;

